import { Icon } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import UsersApi from "Classes/API/UsersApi";
import SearchUserReq from "Classes/Models/Requests/Users/SearchUser";
import SearchStringOrIntReq from "Classes/Models/Requests/StringIntSearchReq";
import { UserContext } from "App";
import AddmissionButton from "layouts/AccountingSessions/AddmissionButton";
import PackageStatistics from "../../../AccountingSessions/Packages/PackageDetails/PackageStatistics";
import {
  STATUS_THEMES,
  BADGE_STYLES,
  SYSTEM_COLORS,
} from "utils/themeConstants";
import { cardLayoutStyles } from "../../../../utils/tableStyles";

const ALLOWED_ROLES = ["SuperAdmin", "Admin", "Owner", "HeadAccounting"];

const AccSessionDiv = ({
  AccSession,
  EnableFacilityModal,
  GoToAccSessionDetails,
  DeleteAppointment,
  setMovingSession,
  setIsLoaded,
  AppHour,
  isDummy,
  hasReminder,
  isOperationsEnabled,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useContext(UserContext);
  const [packageData, setPackageData] = useState(null);
  const [currentSession] = useState(AccSession);

  const showWhatsapp =
    currentSession.sessionName !== "Message" &&
    ALLOWED_ROLES.includes(user?.userType);

  const updateUrlAndNavigate = (path, extraState = {}) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("targetId", "AccSessionDiv" + currentSession.id);
    window.history.replaceState(
      null,
      "",
      `${location.pathname}?${searchParams.toString()}`,
    );

    if (path && path !== "#") {
      navigate(path, { state: { ...location.state, ...extraState } });
    }
  };

  const toggleAbsent = async (sessionId) => {
    const req = new SearchStringOrIntReq();
    req.SearchInt = sessionId;
    const res = await SessionsApi.ToggleAccountingSessionIsAbsent(req);
    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success");
      if (setIsLoaded) setIsLoaded(false);
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const sendWhatsapp = async () => {
    let req = new SearchUserReq();
    req.patientId = currentSession.patientId;

    Swal.fire({
      title: "Preparing Message...",
      didOpen: () => Swal.showLoading(),
    });

    let res = await UsersApi.SearchUsers(req);
    Swal.close();

    if (res.status === "Success") {
      let patient = res.data.list[0];
      let phone = patient.number.replace(/[^\d]/g, "");
      let sessionName = currentSession.sessionName.split(" -")[0];

      let appointmentDate = new Date(AppHour.appointmentHourTime);
      let hours = appointmentDate.getHours();
      let minutes = appointmentDate.getMinutes().toString().padStart(2, "0");
      let ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      let message = `Dear ${patient.name}, this is an automated message from Elagi Center to confirm your appointment at ${hours}:${minutes} ${ampm} on ${appointmentDate.toLocaleDateString()} with ${currentSession.therapistName} for ${sessionName}.`;

      window.open(
        `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`,
        "_blank",
      );
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const showPackageStats = async (packageId) => {
    try {
      Swal.fire({
        title: "Loading...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const req = new SearchStringOrIntReq();
      req.SearchInt = packageId;
      const res = await SessionsApi.GetPackageById(req);
      Swal.close();
      setPackageData(res.data);
    } catch (error) {
      Swal.fire("Error", "Failed to load package data", "error");
    }
  };

  const statusTheme = useMemo(() => {
    if (isDummy) return STATUS_THEMES.SECONDARY;
    if (currentSession.isAdmitted) return STATUS_THEMES.SUCCESS;
    if (currentSession.isAbsent) return STATUS_THEMES.DANGER;
    if (!currentSession.isPaidFor) return STATUS_THEMES.WARNING;
    return STATUS_THEMES.INFO;
  }, [currentSession, isDummy]);

  const payment = useMemo(() => {
    if (isDummy) {
      if (currentSession.sessionName === "Message") {
        return { config: BADGE_STYLES.dummy, text: "MESSAGE" };
      }
      return { config: BADGE_STYLES.dummy, text: "DUMMY" };
    }
    const isOverdue = currentSession.packageRefundableAmount < 0;
    return {
      config: isOverdue ? BADGE_STYLES.danger : BADGE_STYLES.success,
      text: isOverdue ? "PAYMENT REQUIRED" : "ACCOUNT CLEAR",
    };
  }, [currentSession, isDummy]);

  const sessionCount = useMemo(() => {
    if (isDummy) {
      if (currentSession.sessionName === "Message") {
        return null;
      }
      return { config: BADGE_STYLES.dummy, text: "DUMMY SESSION" };
    }
    const count = currentSession.availableRemainingSessions;
    return {
      config: count >= 2 ? BADGE_STYLES.danger : BADGE_STYLES.success,
      text: count != null ? `${count} SESSIONS LEFT` : "ACTIVE PLAN",
    };
  }, [currentSession, isDummy]);

  if (currentSession.sessionName === "Message") {
    return (
      <div
        id={`AccSessionDiv${currentSession.id}`}
        style={{
          marginBottom: "16px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
              opacity: 0.85,
            }}
          >
            <Icon style={{ fontSize: "16px" }}>chat</Icon>
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Note
            </span>
          </div>

          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: "600",
              lineHeight: "1.4",
            }}
          >
            {currentSession.patientName}
          </div>

          {/* ID footer */}
          <div
            style={{
              marginTop: "8px",
              fontSize: "0.65rem",
              opacity: 0.7,
              textAlign: "right",
            }}
          >
            ID: {currentSession.id}
          </div>
        </div>

        {isOperationsEnabled && (
          <div
            style={{
              display: "flex",
              gap: "6px",
              marginTop: "6px",
              marginLeft: "8px",
            }}
          >
            <button
              className="btn btn-sm"
              onClick={() => setMovingSession(currentSession)}
              title="Move Message"
              style={{
                background: "#f0f0f0",
                border: "1px solid #ddd",
                borderRadius: "20px",
                padding: "4px 12px",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#555",
                cursor: "pointer",
              }}
            >
              <Icon style={{ fontSize: "14px" }}>content_cut</Icon>
              Move
            </button>
            <button
              className="btn btn-sm"
              onClick={() => DeleteAppointment(currentSession, isDummy)}
              title="Delete Message"
              style={{
                background: "#fff5f5",
                border: "1px solid #fed7d7",
                borderRadius: "20px",
                padding: "4px 12px",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#e53e3e",
                cursor: "pointer",
              }}
            >
              <Icon style={{ fontSize: "14px" }}>delete</Icon>
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      id={`AccSessionDiv${currentSession.id}`}
      style={cardLayoutStyles.container(statusTheme.color)}
    >
      <div style={cardLayoutStyles.header(statusTheme.bg, statusTheme.color)}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: "900",
              color: statusTheme.color,
            }}
          >
            ID: {currentSession.id}
          </span>
          {hasReminder && (
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: SYSTEM_COLORS.danger,
                boxShadow: `0 0 5px ${SYSTEM_COLORS.danger}`,
              }}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            color: statusTheme.color,
          }}
        >
          <Icon sx={{ fontSize: "1.1rem" }}>{statusTheme.icon}</Icon>
          <b style={{ fontSize: "0.7rem", textTransform: "uppercase" }}>
            {currentSession.isAdmitted
              ? "Admitted"
              : currentSession.isAbsent
                ? "Absent"
                : "Scheduled"}
          </b>
        </div>
      </div>

      <div style={cardLayoutStyles.body}>
        <div
          style={{
            fontSize: "1.1rem",
            fontWeight: "800",
            color: SYSTEM_COLORS.dark,
          }}
        >
          {currentSession.patientName}
        </div>
        <div style={{ fontSize: "0.8rem", color: SYSTEM_COLORS.muted }}>
          {currentSession.sessionName}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            margin: "8px 0",
          }}
        >
          {currentSession.getSessionFacilities?.map((f) => (
            <span key={f.id} style={cardLayoutStyles.tag}>
              {f?.getFacility?.name}
            </span>
          ))}
        </div>

        <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
          {payment && (
            <div style={cardLayoutStyles.badge(payment.config)}>
              {payment.text}
            </div>
          )}
          {sessionCount && (
            <div style={cardLayoutStyles.badge(sessionCount.config)}>
              {sessionCount.text}
            </div>
          )}
        </div>
        {isOperationsEnabled && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "4px",
                marginBottom: "8px",
              }}
            >
              <button
                className="btn btn-sm btn-outline-light text-dark border"
                onClick={() => EnableFacilityModal(true, currentSession)}
              >
                <Icon sx={{ fontSize: 17 }}>category</Icon>
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  updateUrlAndNavigate("#");
                  GoToAccSessionDetails(currentSession);
                }}
              >
                <Icon sx={{ fontSize: 20 }}>info</Icon>
              </button>

              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => setMovingSession(currentSession)}
              >
                <Icon sx={{ fontSize: 16 }}>{isDummy ? "content_copy" : "content_cut"}</Icon>
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => DeleteAppointment(currentSession, isDummy)}
              >
                <Icon sx={{ fontSize: 16 }}>delete</Icon>
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "4px",
                marginBottom: "8px",
              }}
            >
              <button
                className="btn btn-xs btn-success"
                onClick={() =>
                  updateUrlAndNavigate(
                    `../CreateEntry/${currentSession.patientId}`,
                    { accSessionId: currentSession.id },
                  )
                }
              >
                + ENTRY
              </button>
              <button
                className="btn btn-xs btn-outline-dark"
                onClick={() =>
                  updateUrlAndNavigate(
                    `/PatientHistory/${currentSession.patientId}`,
                  )
                }
              >
                HISTORY
              </button>
            </div>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {!isDummy && user.userType !== "SubAdmin" && (
                <button
                  className={`btn btn-xs ${currentSession.isAbsent ? "btn-dark" : "btn-outline-danger"}`}
                  onClick={() => toggleAbsent(currentSession.id)}
                  style={{ flex: 2 }}
                >
                  {currentSession.isAbsent ? "MARK PRESENT" : "MARK ABSENT"}
                </button>
              )}
              {!isDummy && user?.userType !== "Therapist" && (
                <AddmissionButton AccSession={currentSession} />
              )}
              {showWhatsapp && (
                <button
                  className="btn btn-xs btn-success"
                  onClick={sendWhatsapp}
                >
                  <WhatsAppIcon sx={{ fontSize: 14 }} />
                </button>
              )}
              {currentSession.packageId && (
                <button
                  className="btn btn-xs btn-info"
                  onClick={() => showPackageStats(currentSession.packageId)}
                >
                  <Icon sx={{ fontSize: 14 }}>assessment</Icon>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {packageData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setPackageData(null)}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "16px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <PackageStatistics Package={packageData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccSessionDiv;
